/**
 * Homepage
 */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import autobind from 'class-autobind';
import classNames from 'classnames';

import { capitalize, every, get, filter, isEmpty, isEqual, isObject, isString, merge, take } from 'lodash';
import moment from 'moment';
import { dataLayer, isEecPromo } from 'utils/analytics';


import CollectionCarousel from 'components/CollectionCarousel';
import HomepageTile from 'components/Homepage/HomepageTile';
import Responsive from 'components/Responsive';
import SiteFooter from 'components/SiteFooter';

// Selectors
import { createStructuredSelector } from 'reselect';

import {
	getHomepage as getHomepageAction
} from './actions';

import {
	selectHomepage
} from './selectors';

import {
	selectAuthState,
	selectBreakpoints,
	selectFavoritedPackageIDs
} from 'pages/App/selectors';

import {
	subscribeNewsletter as subscribeNewsletterAction,
	toggleFavorite as toggleFavoriteAction
} from 'pages/App/actions';


import { loadWOFFs } from 'utils';

import styles from './styles.css';


export class Homepage extends React.Component {
	isMounted = true;

	constructor(props) {
		super(props);
		autobind(this);
	}

	state = {
		layout: null,
		fontsLoaded: false,
		loadedTiles: {
			mobile: null,
			tablet: null,
			desktop: null
		}
	};

	componentDidMount() {
		const { getHomepage, params } = this.props;
		getHomepage(params);
		this.setCurrentLayout();

		this.isMounted = true;

		if (this.isMounted) {
			this.loadWOFFs();
		}
	}

	componentDidUpdate(prevProps, prevState) {
		const { breakpoints, homepage } = this.props;
		const { layout } = this.state;
		if (!isEqual(breakpoints, prevProps.breakpoints)) {
			this.setCurrentLayout();
		}

		if ((!isEmpty(homepage) && !isEqual(homepage, prevProps.homepage)) || (layout && layout !== prevState.layout)) {
			this.dataLayerPromoImpression();
		}
	}

	componentWillUnmount() {
		this.isMounted = false;
	}

	setCurrentLayout() {
		const { breakpoints, homepage } = this.props;
		const { loadedTiles } = this.state;

		let layout = 'mobile';

		if (breakpoints.gtMedium) {
			layout = 'tablet';
		}

		if (breakpoints.gtLarge) {
			layout = 'desktop';
		}

		if (!isEmpty(loadedTiles[layout])) {
			this.setState({ layout });
		} else {
			const modules = get(homepage, layout, []);
			this.setState(merge({}, this.state, { layout, loadedTiles: { [layout]: new Array(modules.length) } }));
		}
	}

	dataLayerPromoImpression() {
		// const { homepage } = this.props;
		// const { layout } = this.state;

		// let modules = get(homepage, layout, []);
		// modules = filter(modules, (module) => isEecPromo(module.moduleLink));
		// if (modules.length > 0) {
		// 	dataLayer({
		// 		event: 'eec_promo_impression',
		// 		eventCat: 'ecommerce',
		// 		eventAct: 'promotion impression',
		// 		eventVal: 0,
		// 		nonInteraction: true,
		// 		ecommerce: {
		// 			promoView: {
		// 				promotions: modules.map((module) => {
		// 					const { id, position, title: name } = module;
		// 					return {
		// 						creative: 'homepageTile',
		// 						id: `homepageTile_${id}`,
		// 						name,
		// 						position: `${position}`
		// 					};
		// 				})
		// 			}
		// 		}
		// 	});
		// }
	}

	async loadWOFFs() {
		const woffs = [
			'Ideal Sans SSm Extra Light',
			'Ideal Sans SSm Extra Light Italic',
			'Ideal Sans SSm Bold',
			'Ideal Sans SSm Bold Italic',
			'Sentinel Bold',
			'Sentinel SSm Book',
			'Sentinel SSm Book Italic',
			'Sentinel SSm Bold',
			'Sentinel SSm Bold Italic'
		];

		await loadWOFFs(woffs)
			.finally(() => {
				if (this.isMounted) {
					this.setState({ fontsLoaded: true });
				}
			});
	}

	hasSrcAsset(asset) {
		return (isObject(asset) && !isEmpty(get(asset, 'image'))) || (isString(asset) && !isEmpty(asset));
	}

	formatAsset(asset, type, size, poster, height, width) {
		return this.hasSrcAsset(asset) ? { asset, type, size, poster, height, width } : null;
	}

	render() {
		const {
			breakpoints,
			favoritedPackageIDs,
			homepage,
			isAuthenticated,
			subscribeNewsletter,
			toggleFavorite
		} = this.props;
		const { fontsLoaded, layout } = this.state;

		if (!homepage) {
			return null;
		}

		const modules = get(homepage, layout, []);
		const carousel = get(homepage, `[${layout}Carousel]`);

		return (
			<div className={ classNames(styles.homepage, fontsLoaded && styles.fontsLoaded) }>
				<header className={ styles.header }>
					<Responsive>
						<div className={ styles.headerInner }>
							<h1>typography.com</h1>
							<time>{ moment().format('D MMMM YYYY') }</time>
						</div>
					</Responsive>
				</header>
				{ !isAuthenticated &&
					<SiteFooter
						breakpoints={ breakpoints }
						className={ styles.footer }
						onSubmit={ subscribeNewsletter }
						step="expanded"
					/>
				}
				<div className={ styles.tiles }>
					{
						modules && modules.map((module, index) => {
							if (isEmpty(module)) {
								return null;
							}
							const {
								backgroundColor, bgColorHvr,
								headline, headlineColor, headlineColorHvr,
								id, imageEffects, imageEffectsHvr, isAnimatedSvg, moduleLink, position,
								scrollPercentage, size, svgLayerColors, text, textColor, textColorHvr,
								title, urlTitle, videoOnMobile
							} = module;

							const onLoad = () => {
								this.setState(state => {
									const loadedTiles = [...this.state.loadedTiles[layout]];
									loadedTiles[index] = true;
									return merge({}, state, { loadedTiles: { [layout]: loadedTiles } });
								});
							};

							const isVisible = index === 0 ? true : every(take(this.state.loadedTiles[layout], index), (loaded) => loaded === true);

							const assets = {};
							['single', 'double', 'triple'].forEach((assetSize) => {
								assets[assetSize] = {};
								['embed', 'image', 'video'].forEach((type) => {
									const poster = this.formatAsset(module[`${assetSize}Image`]);
									const posterHover = this.formatAsset(module[`${assetSize}ImageHover`]);
									const height = module[`${assetSize}AssetH`];
									const width = module[`${assetSize}AssetW`];
									assets[assetSize][type] = {
										asset: this.formatAsset(module[`${assetSize}${capitalize(type)}`], type, assetSize, poster, height, width),
										assetHover: this.formatAsset(module[`${assetSize}${capitalize(type)}Hover`], type, assetSize, posterHover || poster, height, width)
									};
								});
							});

							return (
								<HomepageTile
									assets={ assets }
									backgroundColor={ backgroundColor }
									backgroundColorHover={ bgColorHvr }
									fontsLoaded={ fontsLoaded }
									headline={ headline }
									headlineColor={ headlineColor }
									headlineColorHover={ headlineColorHvr }
									homepageUrlTitle={ homepage.urlTitle }
									id={ id }
									imageEffects={ imageEffects }
									imageEffectsHover={ imageEffectsHvr }
									isAnimatedSVG={ isAnimatedSvg === 1 }
									isVisible={ fontsLoaded && isVisible }
									key={ urlTitle }
									layout={ layout }
									moduleLink={ moduleLink }
									onLoad={ onLoad }
									position={ position }
									scrollPercentage={ scrollPercentage }
									size={ size }
									svgLayerColors={ svgLayerColors }
									text={ text }
									textColor={ textColor }
									textColorHover={ textColorHvr }
									title={ title }
									urlTitle={ urlTitle }
									videoOnMobile={ videoOnMobile === 'y' }
								/>
							);
						})
					}
				</div>
				<section className={ styles.carouselContainer }>
					<header className={ styles.carouselHeader }>
						<h3>{ homepage.carouselEyebrow }</h3>
						<h2>{ homepage.carouselTitle }</h2>
					</header>
					{
						get(carousel, '[0].content.collectionCarousel') &&
						<CollectionCarousel
							breakpoints={ breakpoints }
							className={ styles.carousel }
							collectionCarousel={ carousel[0].content.collectionCarousel }
							creative="homepageCarousel"
							favoritedPackageIDs={ favoritedPackageIDs }
							name={ homepage.carouselTitle }
							onFavorite={ toggleFavorite }
						/>
					}
				</section>
			</div>
		);
	}
}

Homepage.propTypes = {
	breakpoints: PropTypes.object.isRequired,
	getHomepage: PropTypes.func.isRequired,
	favoritedPackageIDs: PropTypes.array,
	homepage: PropTypes.object,
	isAuthenticated: PropTypes.bool,
	params: PropTypes.object.isRequired,
	subscribeNewsletter: PropTypes.func.isRequired,
	toggleFavorite: PropTypes.func.isRequired
};

Homepage.defaultProps = {
	favoritedPackageIDs: null,
	homepage: null,
	isAuthenticated: false
};

const mapStateToProps = createStructuredSelector({
	breakpoints: selectBreakpoints(),
	favoritedPackageIDs: selectFavoritedPackageIDs(),
	homepage: selectHomepage(),
	isAuthenticated: selectAuthState()
});

function mapDispatchToProps(dispatch) {
	return {
		getHomepage: (data) => dispatch(getHomepageAction(data)),
		subscribeNewsletter: (data, callback) => dispatch(subscribeNewsletterAction(data, callback)),
		toggleFavorite: (data, callback) => dispatch(toggleFavoriteAction(data, callback)),
		dispatch
	};
}

export default connect(mapStateToProps, mapDispatchToProps)(Homepage);
